import React, { useCallback, useEffect, useRef, useState } from 'react';
import { AssigneeModel } from '@/typings/models/Assignee';
import { LeadTaskModel } from '@/typings/models/LeadTask';
import { fetchLeadTasksList } from '@/services/api/leadTask';
import { TasksList } from '@/pages/Tasks/TasksList';
import { fetchAllPages } from '@/utils/pagination';

const TASKS_PAGE_SIZE = 200;

interface AgentTasksTabProps {
  agent: AssigneeModel;
}

const AgentTasksTab: React.FC<AgentTasksTabProps> = ({ agent }) => {
  const [tasks, setTasks] = useState<LeadTaskModel[]>([]);
  const [loading, setLoading] = useState(false);
  const [showDone, setShowDone] = useState(false);
  const latestRequest = useRef(0);

  // Open tasks are filtered server-side: an unfiltered first page of a busy
  // agent can be entirely completed tasks, which left the tab empty although
  // open ones existed on later pages.
  const fetchTasks = useCallback(() => {
    if (!agent?.id) return;

    const requestId = ++latestRequest.current;
    setLoading(true);
    fetchAllPages<LeadTaskModel>((page) =>
      fetchLeadTasksList({
        query: {
          assignee_id: agent.id,
          page_size: TASKS_PAGE_SIZE,
          page,
          ...(showDone ? {} : { is_open: 1 }),
        },
      }),
    )
      .then(({ items }) => {
        if (requestId === latestRequest.current) setTasks(items);
      })
      .finally(() => {
        if (requestId === latestRequest.current) setLoading(false);
      });
  }, [agent?.id, showDone]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  return (
    <TasksList
      data={tasks}
      request={fetchTasks}
      loading={loading}
      showDone={showDone}
      onShowDoneChange={setShowDone}
      hideCreate
      showAssignee={false}
      showLead={true}
      showDescription={true}
    />
  );
};

export default AgentTasksTab;
